import React, { createContext, useState, useEffect, ReactNode, useContext, useRef } from 'react';
import { Monad } from '../monad/Monad';
import { useAuthContext } from './AuthContext';
import FullCalendar from '@fullcalendar/react';
import { CalendarApi } from '@fullcalendar/core';
import { ApiBooking, ApiEvent, useHttpClientContext } from './HttpClientContext';
import { useTranslation } from 'react-i18next';

export class EventModel {
  eventId: string;
  name: string;
  heading: string;
  startDate: string;
  startTime: string;
  endTime: string;
  description: string;
  location?: string;
  preBookingReq: boolean;
  visibleParticipantList: boolean;
  allowedToCheckIn: string[];
  isPersonal: boolean;
  checkInPossible: boolean;
  mainEventId?: string;
  source: 'xano' | 'minaaktiviteter';
  maEventId?: string;
  endDate?: string;
  eventGroup?: string;

  booking?: BookingModel;

  constructor({ apiEvent, apiBooking }: { apiEvent: ApiEvent, apiBooking?: ApiBooking }) {
    this.eventId = apiEvent.eventId;
    this.name = apiEvent.name;
    this.heading = apiEvent.heading;
    this.startDate = apiEvent.startDate;
    this.startTime = apiEvent.startTime;
    this.endTime = apiEvent.endTime;
    this.description = apiEvent.description;
    this.location = apiEvent.location;

    this.preBookingReq = apiEvent.preBookingReq === "yes";
    this.visibleParticipantList = apiEvent.visibleParticipantList === "yes";

    if (apiEvent.allowedToCheckIn && apiEvent.allowedToCheckIn.length) {
      this.allowedToCheckIn = apiEvent.allowedToCheckIn.split(",");
    } else {
      this.allowedToCheckIn = [];
    }

    this.isPersonal = apiEvent.isPersonal === "yes";
    this.checkInPossible = apiEvent.checkInPossible === "yes";
    this.mainEventId = apiEvent.mainEventId;
    this.source = apiEvent.source ?? 'xano';
    this.maEventId = apiEvent.maEventId;
    this.endDate = apiEvent.endDate;
    this.eventGroup = apiEvent.eventGroup;

    if (apiBooking) {
      this.booking = new BookingModel({ apiBooking: apiBooking });
    }
  }
}

class BookingModel {
  eventId: string;
  userKey: string;
  name: string;
  bookingInfo: string;
  partnerName?: string;
  partnerBookingInfo?: string;
  //visibleForOthers: boolean;
  booked: boolean;
  partnerBooked: boolean;
  checkedIn: boolean;

  constructor({ apiBooking }: { apiBooking: ApiBooking }) {
    this.eventId = apiBooking.eventId;
    this.userKey = apiBooking.userKey;
    this.name = apiBooking.name;
    this.bookingInfo = apiBooking.bookingInfo;
    this.partnerName = apiBooking.partnerName;
    this.partnerBookingInfo = apiBooking.partnerBookingInfo;
    //this.visibleForOthers = apiBooking.visibleForOthers === "Ja";
    this.booked = apiBooking.booked === "yes";
    this.partnerBooked = apiBooking.partnerBooked === "yes";
    this.checkedIn = apiBooking.checkedIn === "yes";
  }
}

export interface EventContextType {
  events: Monad<EventModel[]>;
  fetchEventModels: () => Promise<void>;
  eventModalOpen: boolean;
  openEventModal: (eventId: String) => void;
  closeEventModal: () => void;

  selectedEvent?: EventModel;

  displayAllEvents: boolean
  toggleDisplayAllEvents: (on: boolean) => void;
  showPersonalEvents: boolean;
  toggleShowPersonalEvents: (on: boolean) => void;
  calendarDate: Date;
  changeCalendarDate: (date: Date) => void;

  calendarRef: React.RefObject<FullCalendar | null>;
  changeCalendarView: (view: "listWeek" | "timeGridDay") => void;
  calendarView: "listWeek" | "timeGridDay";

  eventMemberMainEventIds: string[];
  isEventMemberOf: (event: EventModel) => boolean;

  showMaEvents: boolean;
  toggleShowMaEvents: (on: boolean) => void;
}

export const EventContext = createContext<EventContextType | undefined>(undefined);

const DISPLAY_ALL_EVENTS_STORAGE_KEY = "display_all_events";
const SHOW_PERSONAL_EVENTS_STORAGE_KEY = "show_personal_events";
const SHOW_MA_EVENTS_STORAGE_KEY = "show_ma_events";

const readPersistedDisplayAllEvents = (): boolean => {
  const stored = localStorage.getItem(DISPLAY_ALL_EVENTS_STORAGE_KEY);
  if (stored === "true") return true;
  if (stored === "false") return false;
  return true;
};

interface EventProviderProps {
  children: ReactNode;
}

export const useEventContext = (): EventContextType => {
  const eventsContext = useContext<EventContextType | undefined>(EventContext);
  if (!eventsContext) {
    throw new Error("eventsContext is undefined");
  }
  return eventsContext;
}

export const constructCalendarUrlWithQuery = (view: string | null, displayAll: boolean, date: Date)  => {
  const formattedDate = formatDateString(date || new Date());
  return `/?view=${view === "timeGridDay" ? "timeGridDay" : "listWeek"}&displayAll=${displayAll}&date=${formattedDate}`;
}

export const formatDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const [events, setEvents] = useState<Monad<EventModel[]>>(Monad.idle());
  //const [bookings, setBookings] = useState<Monad<ApiBooking[]>>(Monad.idle());

  const [displayAllEvents, setDisplayAllEvents] = useState<boolean>(readPersistedDisplayAllEvents) //TODO: change default to false
  const [showPersonalEvents, setShowPersonalEvents] = useState<boolean>(localStorage.getItem(SHOW_PERSONAL_EVENTS_STORAGE_KEY) === "true");
  const [eventModalOpen, setEventModalOpen] = useState<boolean>(false)
  const [selectedEvent, setSelectedEvent] = useState<EventModel | undefined>(undefined)
  const [calendarView, setCalendarView] = useState<"listWeek" | "timeGridDay">("listWeek")
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [eventMemberMainEventIds, setEventMemberMainEventIds] = useState<string[]>([]);
  const [showMaEvents, setShowMaEvents] = useState<boolean>(localStorage.getItem(SHOW_MA_EVENTS_STORAGE_KEY) === "true");

  const authContext = useAuthContext();
  const httpClientContext = useHttpClientContext();

  const calendarRef = useRef<FullCalendar | null>(null);

  const { i18n } = useTranslation();

  const openEventModal = (eventId: String) => {
    console.log("openEventModal", eventId);
    const selectedEvent = events.map((data) => data.find(evt => evt.eventId === eventId)).getOrElse(undefined);
    setSelectedEvent(selectedEvent);
    setEventModalOpen(true);
  }

  const closeEventModal = () => {
    setEventModalOpen(false);
    setSelectedEvent(undefined);
  }

  const toggleDisplayAllEvents = (on: boolean) => {
    localStorage.setItem(DISPLAY_ALL_EVENTS_STORAGE_KEY, String(on));
    setDisplayAllEvents(on);
    //TODO: shame .. useNavigate not available in this context
    window.location.href = `/#${constructCalendarUrlWithQuery(calendarView, on, calendarDate)}`;
  }

  const toggleShowPersonalEvents = (on: boolean) => {
    localStorage.setItem(SHOW_PERSONAL_EVENTS_STORAGE_KEY, String(on));
    setShowPersonalEvents(on);
  }

  const toggleShowMaEvents = (on: boolean) => {
    localStorage.setItem(SHOW_MA_EVENTS_STORAGE_KEY, String(on));
    setShowMaEvents(on);
  }

  const changeCalendarView = (view: "listWeek" | "timeGridDay") => {
    const calendarApi: CalendarApi | undefined = calendarRef?.current?.getApi();

    if (calendarApi) {
      calendarApi.changeView(view);
      setCalendarView(view);
    }
  }

  const changeCalendarDate = (date: Date) => {
    setCalendarDate(date);
  }

  const isEventMemberOf = (event: EventModel): boolean => {
    const result = event.mainEventId != null && eventMemberMainEventIds.includes(event.mainEventId);
    console.log("isEventMemberOf", event.eventId, event.mainEventId, eventMemberMainEventIds, result);
    return result;
  }

  const fetchEventModels = async () => {
    setEvents(Monad.loading());

    //TODO: UGLY!! this fetches events 2 times after login ..
    //      also fix the duplicated fetch logic. Maybe cache events 
    await authContext.user.match({
      loggedIn: async (user) => {
        try {
          var apiBookings: ApiBooking[] = [];

          if (!user.expired) {
            const apiBookingsResponse = await httpClientContext.authenticatedHttpClient.get<ApiBooking[]>('my-bookings', {
              headers: {
                "Authorization": `Bearer ${user?.access_token}`
              }
            });
            apiBookings = apiBookingsResponse.data;
          }

          const today = formatDateString(new Date());
          const [xanoResult, maResult] = await Promise.allSettled([
            httpClientContext.anonymousHttpClient.get<ApiEvent[]>('events'),
            showMaEvents
              ? httpClientContext.authenticatedHttpClient.get<ApiEvent[]>('/ma-proxy/ma-events', { params: { minDate: today } })
              : Promise.resolve({ data: [] as ApiEvent[] })
          ]);

          const xanoEvents = xanoResult.status === 'fulfilled'
            ? xanoResult.value.data.map(evt => ({ ...evt, source: evt.source ?? 'xano' as const }))
            : [];
          const maEvents = maResult.status === 'fulfilled'
            ? maResult.value.data.map(evt => ({ ...evt, source: 'minaaktiviteter' as const }))
            : [];
          if (maResult.status === 'rejected') {
            console.error("Error loading MA events", maResult.reason);
          }

          const allApiEvents = [...xanoEvents, ...maEvents].filter(event => Boolean(event.eventId));

          // Användaren måste vara anmäld till själva huvud-eventet för att få tillgång till dess sub-events.
          if (apiBookings.length > 0) {
            const bookedEventIds = new Set(apiBookings.map(b => b.eventId));
            const validMainEventIds = Array.from(new Set(
              xanoEvents
                .filter(e => e.mainEventId != null && bookedEventIds.has(e.eventId))
                .map(e => e.mainEventId as string)
            ));
            setEventMemberMainEventIds(validMainEventIds);
          } else {
            setEventMemberMainEventIds([]);
          }

          const eventModels = allApiEvents.map(event => new EventModel({
            apiEvent: event,
            apiBooking: apiBookings.find(booking => booking.eventId === event.eventId)
          }));

          setEvents(Monad.success(eventModels));
        } catch (error) {
          console.error("Error loading events or bookings", error);
          setEvents(Monad.error("Kunde inte ladda kalender."));
        }
      },
      loggedOut: async () => {
        try {
          setEventMemberMainEventIds([]);
          const today = formatDateString(new Date());
          const [xanoResult, maResult] = await Promise.allSettled([
            httpClientContext.anonymousHttpClient.get<ApiEvent[]>('events'),
            showMaEvents
              ? httpClientContext.authenticatedHttpClient.get<ApiEvent[]>('/ma-proxy/ma-events', { params: { minDate: today } })
              : Promise.resolve({ data: [] as ApiEvent[] })
          ]);

          const xanoEvents = xanoResult.status === 'fulfilled'
            ? xanoResult.value.data.map(evt => ({ ...evt, source: evt.source ?? 'xano' as const }))
            : [];
          const maEvents = maResult.status === 'fulfilled'
            ? maResult.value.data.map(evt => ({ ...evt, source: 'minaaktiviteter' as const }))
            : [];
          if (maResult.status === 'rejected') {
            console.error("Error loading MA events", maResult.reason);
          }

          const allApiEvents = [...xanoEvents, ...maEvents].filter(event => Boolean(event.eventId));

          const eventModels = allApiEvents.map(event => new EventModel({
            apiEvent: event,
          }));

          setEvents(Monad.success(eventModels));
        } catch (error) {
          console.error("Error loading events or bookings", error);
          setEvents(Monad.error("Kunde inte ladda kalender."));
        }
      }
    });
  }

  useEffect(() => {
    fetchEventModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    //fetchEvents();
    fetchEventModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authContext.user]); //authContext.user //TODO: behöver vi denna?

  useEffect(() => {
    fetchEventModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMaEvents]);

  return (
    <EventContext.Provider value={{
      events,
      fetchEventModels,
      eventModalOpen,
      openEventModal,
      closeEventModal,
      selectedEvent,
      displayAllEvents,
      toggleDisplayAllEvents,
      showPersonalEvents,
      toggleShowPersonalEvents,
      calendarRef,
      changeCalendarView,
      calendarView,
      changeCalendarDate,
      calendarDate,
      eventMemberMainEventIds,
      isEventMemberOf,
      showMaEvents,
      toggleShowMaEvents
    }}>
      {children}
    </EventContext.Provider>
  );
};
